import React from 'react';
import Style from './panelStyles.module.scss';
import { useNode } from '@craftjs/core';
import { PanelContainer } from './uiKit/panelContainer/panelContainer';
import { ControlsGroup } from './uiKit/controlsGroup/controlsGroup';
import { SettingsInput } from './uiKit/input/settingsInput';
import { hasSection, hexToRgba, rgbaToHex, getOpacityFromRgba, clamp } from './helpers/helper';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';

type StylesSection = 'BackgroundColor' | 'Border' | 'Color';

export type StylesProps = {
  sections: StylesSection[];
};

export const defaultBorderStyle: React.CSSProperties = {
  borderColor: hexToRgba('#DFE2E2'),
  borderWidth: 1,
  borderStyle: 'solid',
};

export const StylesPanel = ({ sections }: StylesProps) => {
  const { t } = useScopedTranslation('GeneralFormBuilder.Panels.Styles');
  const {
    actions: { setCustom },
    styles,
  } = useNode((node) => ({
    styles: node.data.custom,
  }));

  return (
    <PanelContainer header={t('Header')} panelName={'Styles'}>
      {hasSection(sections, 'BackgroundColor') && (
        <ControlsGroup
          header={t('BackgroundColor')}
          collapsible={true}
          contentClassName={Style.gridContainer}
          expanded={styles.backgroundColor}
          onOpen={() => {
            setCustom((prop: React.CSSProperties) => {
              prop.backgroundColor = hexToRgba('#F5F7FA');
            }, 500);
          }}
          onClose={() =>
            setCustom((prop: React.CSSProperties) => {
              prop.backgroundColor = undefined;
            }, 500)
          }
        >
          <SettingsInput
            type={'color'}
            inputClassName={Style.colorField}
            value={rgbaToHex(styles.backgroundColor).toUpperCase()}
            onChange={(e) =>
              setCustom((prop: React.CSSProperties) => {
                prop.backgroundColor = hexToRgba(e.target.value);
              }, 500)
            }
          />
          <SettingsInput
            type={'number'}
            units={'%'}
            className={Style.fitContent}
            inputClassName={Style.opacityField}
            defaultValue={getOpacityFromRgba(styles.backgroundColor)}
            onChange={(e) => {
              setCustom((prop: React.CSSProperties) => {
                prop.backgroundColor = hexToRgba(rgbaToHex(prop.backgroundColor), clamp(+e.target.value, 0, 100) / 100);
              }, 500);
            }}
          />
        </ControlsGroup>
      )}

      {hasSection(sections, 'Color') && (
        <ControlsGroup
          header={t('Color')}
          collapsible={true}
          contentClassName={Style.gridContainer}
          expanded={styles.color}
          onOpen={() =>
            setCustom((prop: React.CSSProperties) => {
              prop.color = hexToRgba('#001311');
            }, 500)
          }
          onClose={() =>
            setCustom((prop: React.CSSProperties) => {
              prop.color = undefined;
            }, 500)
          }
        >
          <SettingsInput
            type={'color'}
            inputClassName={Style.colorField}
            value={rgbaToHex(styles.color).toUpperCase()}
            onChange={(e) =>
              setCustom((prop: React.CSSProperties) => {
                prop.color = hexToRgba(e.target.value);
              }, 500)
            }
          />
          <SettingsInput
            type={'number'}
            units={'%'}
            className={Style.fitContent}
            inputClassName={Style.opacityField}
            defaultValue={getOpacityFromRgba(styles.color)}
            onChange={(e) => {
              setCustom((prop: React.CSSProperties) => {
                prop.color = hexToRgba(rgbaToHex(prop.color), clamp(+e.target.value, 0, 100) / 100);
              }, 500);
            }}
          />
        </ControlsGroup>
      )}

      {hasSection(sections, 'Border') && (
        <ControlsGroup
          header={t('Border')}
          collapsible={true}
          contentClassName={Style.gridContainer}
          expanded={styles.borderColor || styles.borderWidth !== undefined || styles.borderStyle}
          onOpen={() => {
            setCustom((prop: React.CSSProperties) => {
              prop.borderColor = defaultBorderStyle.borderColor;
              prop.borderWidth = defaultBorderStyle.borderWidth;
              prop.borderStyle = defaultBorderStyle.borderStyle;
            }, 500);
          }}
          onClose={() =>
            setCustom((prop: React.CSSProperties) => {
              prop.borderColor = undefined;
              prop.borderWidth = undefined;
              prop.borderStyle = undefined;
            }, 500)
          }
        >
          <SettingsInput
            type={'color'}
            inputClassName={Style.colorField}
            value={rgbaToHex(styles.borderColor).toUpperCase()}
            onChange={(e) =>
              setCustom((prop: React.CSSProperties) => {
                prop.borderColor = hexToRgba(e.target.value);
              }, 500)
            }
          />
          <div className={Style.borderFieldContainer}>
            <SettingsInput
              type={'number'}
              units={'%'}
              inputClassName={Style.opacityField}
              defaultValue={getOpacityFromRgba(styles.borderColor)}
              onChange={(e) => {
                setCustom((prop: React.CSSProperties) => {
                  prop.borderColor = hexToRgba(rgbaToHex(prop.borderColor), clamp(+e.target.value, 0, 100) / 100);
                }, 500);
              }}
            />
            <SettingsInput
              type={'number'}
              units={'px'}
              inputClassName={Style.opacityField}
              defaultValue={styles.borderWidth || 0}
              onChange={(e) => {
                setCustom((prop: React.CSSProperties) => {
                  prop.borderStyle = 'solid';
                  prop.borderWidth = clamp(+e.target.value, 0, 20);
                }, 500);
              }}
            />
          </div>
        </ControlsGroup>
      )}
    </PanelContainer>
  );
};
